/**
 * Wave schedule (DESIGN 4.4): the twelve scripted waves, the per-player
 * count multiplier and the endless rule (replay a random scripted wave
 * from 7..11 with scaled HP). Timing constants live in constants.ts and are
 * re-exported here as DESIGN 7.3 lists them.
 *
 * Boss waves (added 2026-10-27, not in DESIGN): every BOSS.every-th wave
 * (5, 10, 15, ...) is the jotunn's, one boss with its HP scaled by the
 * player count plus a small escort. The ordinary waves in between walk the
 * scripted list in order, so wave 6 plays scripted row 5 and no lesson is
 * lost; the run reaches endless at wave 16 instead of 13.
 */

import type { EnemyKind } from './enemies.js';
import {
  BOSS,
  ENDLESS_HP_SCALE_PER_WAVE,
  ENDLESS_SOURCE_WAVES,
  PLAYER_COUNT_MULT,
  SCRIPTED_WAVES,
} from './constants.js';
import { ENEMY_DEFS } from './enemies.js';
import { randInt } from './prng.js';
import type { Rng } from './prng.js';

export { BREATHER_SECONDS, COUNTDOWN_SECONDS, SPAWN_INTERVAL } from './constants.js';

export interface WaveGroup { kind: EnemyKind; count: number }

interface ScriptedWave {
  groups: readonly WaveGroup[];
  /** The "Lesson" column of DESIGN 4.4, for the wave banner. */
  lesson: string;
}

const g = (kind: EnemyKind, count: number): WaveGroup => ({ kind, count });

/** DESIGN 4.4 table, index = wave - 1. Groups spawn in this order, one body per spawn gap (SPAWN_INTERVAL, as difficulty.ts's waveTuning sets it). */
const SCRIPTED: readonly ScriptedWave[] = [
  // Tuned down from 8: the opening wave should teach the nova, not wipe a party still reading the HUD.
  { groups: [g('spider', 5)], lesson: 'Area cast (Shift+LMB with A)' },
  { groups: [g('rogue', 5), g('spider', 4)], lesson: 'Beams, sprays' },
  { groups: [g('rogue', 4), g('ranger', 3)], lesson: 'Range; shield arcs stop arrows' },
  { groups: [g('bomber', 4), g('rogue', 4)], lesson: 'Chain reactions with one fireball' },
  { groups: [g('shaman', 2), g('rogue', 6)], lesson: 'Beam crossing; wet = no lightning' },
  { groups: [g('troll', 1), g('ranger', 4)], lesson: 'Burn the troll or it regenerates' },
  { groups: [g('treespirit', 3), g('spider', 6)], lesson: 'Fire; water is useless' },
  { groups: [g('skeleton', 10), g('shaman', 2)], lesson: 'Life hurts undead, arcane heals them' },
  { groups: [g('orc', 3), g('bomber', 4)], lesson: 'Lightning vs armor; wet + cold shatters' },
  { groups: [g('troll', 2), g('shaman', 2), g('skeleton', 6)], lesson: 'Mixed' },
  { groups: [g('orc', 4), g('treespirit', 3), g('spider', 8)], lesson: 'Mixed' },
  // Was the jotunn + 2 shamans; the jotunn has its own waves now (BOSS_WAVES below).
  { groups: [g('troll', 1), g('orc', 2), g('bomber', 3), g('skeleton', 6)], lesson: 'Everything so far' },
];

const ENDLESS_LESSON = 'Endless: everything you have learned, at once';

interface BossWave {
  /** Escort groups, scaled by the player multiplier like any wave; the jotunn itself is always one. */
  escort: readonly WaveGroup[];
  lesson: string;
}

/**
 * Boss tiers, index = boss number - 1 (wave 5 is the first); later boss
 * waves repeat the last row. Wave 5's bombers can be set off next to the
 * boss (their blasts hurt it), wave 10's shamans wet you for its frost
 * ring, and from wave 15 an orc wall stands in front of it.
 */
const BOSS_WAVES: readonly BossWave[] = [
  { escort: [g('bomber', 2)], lesson: 'Boss: the Jotunn. Read the cracks; it shrugs off lightning' },
  { escort: [g('shaman', 2)], lesson: 'Boss: the Jotunn. Stay dry when the frost ring grows' },
  { escort: [g('orc', 2), g('shaman', 1)], lesson: 'Boss: the Jotunn returns' },
];

/** Waves at or past this replay the endless pool: the first ordinary wave after the scripted list runs out (16). */
export const FIRST_ENDLESS_WAVE: number = (() => {
  let n = 1;
  while (n % BOSS.every === 0 || n - Math.floor(n / BOSS.every) <= SCRIPTED_WAVES) n++;
  return n;
})();

/**
 * Float noise guard for "rounded up": 5 x 1.6 is 8 in arithmetic and must
 * stay 8 (not 9) if a platform's multiply lands a hair above it.
 */
const COUNT_EPS = 1e-9;

/** Wave numbers below 1 (or not finite) read as wave 1; fractions truncate. */
function normaliseWave(wave: number): number {
  return Number.isFinite(wave) ? Math.max(1, Math.floor(wave)) : 1;
}

function scriptedWave(wave: number): ScriptedWave {
  const s = SCRIPTED[wave - 1];
  if (!s) throw new Error(`waves: no scripted wave ${wave}`);
  return s;
}

/** True for every BOSS.every-th wave (5, 10, 15, ...): the jotunn's. */
export function isBossWave(wave: number): boolean {
  return normaliseWave(wave) % BOSS.every === 0;
}

/** 1 for wave 5, 2 for wave 10, ...; 0 for an ordinary wave. */
export function bossTier(wave: number): number {
  const n = normaliseWave(wave);
  return n % BOSS.every === 0 ? n / BOSS.every : 0;
}

function bossWave(tier: number): BossWave {
  const b = BOSS_WAVES[Math.min(tier, BOSS_WAVES.length) - 1];
  if (!b) throw new Error(`waves: no boss tier ${tier}`);
  return b;
}

/**
 * The jotunn's HP multiplier on a boss wave (the server multiplies the
 * def's HP by it, on top of the wave's HP scale): the tier's HP from
 * BOSS.hp over the def's, times the player multiplier. One boss whose HP
 * grows with the party reads better than the 4.4 rule's three jotunns.
 */
export function bossHpMultiplier(wave: number, playerCount: number): number {
  const tier = Math.max(1, bossTier(wave));
  const hp = BOSS.hp[Math.min(tier, BOSS.hp.length) - 1] ?? ENEMY_DEFS.jotunn.hp;
  return (hp / ENEMY_DEFS.jotunn.hp) * playerMultiplier(playerCount);
}

/** The scripted row an ordinary wave plays: boss waves are slotted in between, so wave 6 plays row 5. */
function scriptedRow(n: number): number {
  return n - Math.floor(n / BOSS.every);
}

/** True for ordinary waves past the scripted list (DESIGN 4.1 "then endless scaling"): 16 and up, boss waves aside. */
export function isEndless(wave: number): boolean {
  const n = normaliseWave(wave);
  return !isBossWave(n) && n >= FIRST_ENDLESS_WAVE;
}

/** m(players) of DESIGN 4.4; player counts outside 1..4 clamp to the nearest table entry. */
export function playerMultiplier(playerCount: number): number {
  const n = Number.isFinite(playerCount) ? Math.floor(playerCount) : 1;
  const i = Math.min(Math.max(n, 1), PLAYER_COUNT_MULT.length) - 1;
  return PLAYER_COUNT_MULT[i] ?? 1;
}

/** base x m, rounded up; never below 1 so a group listed in the table always shows up. */
function scaledCount(base: number, m: number): number {
  return Math.max(1, Math.ceil(base * m - COUNT_EPS));
}

/**
 * The difficulty and ramp's extra bodies on top of a group's party-scaled count, rounded to the nearest (rounding up
 * turned wave 7's three tree spirits into four at the first 10%), never fewer than the table's.
 */
function extraCount(n: number, mul: number): number {
  return Math.max(n, Math.round(n * mul - COUNT_EPS));
}

/**
 * Which scripted wave an endless wave replays. With an rng: uniform over
 * ENDLESS_SOURCE_WAVES (the only randomness here, so the server's seed
 * decides). Without one: cycle 7, 8, 9, 10, 11, 7, ... so a caller that has
 * no rng (the client's wave banner, tests) still gets a stable answer.
 */
function endlessSource(wave: number, rng: Rng | undefined): number {
  const span = ENDLESS_SOURCE_WAVES.max - ENDLESS_SOURCE_WAVES.min + 1;
  const offset = rng ? randInt(rng, span) : (wave - FIRST_ENDLESS_WAVE) % span;
  return ENDLESS_SOURCE_WAVES.min + offset;
}

/**
 * Enemy groups for a wave: the scripted table for ordinary waves (base
 * counts x PLAYER_COUNT_MULT[playerCount-1], rounded up), the jotunn plus
 * its scaled escort on boss waves, and a random scripted row from 7..11
 * for endless waves (16+). Pure except endless waves, which take an
 * injected rng so the server stays deterministic. Returns fresh objects so
 * the caller may drain them.
 */
export function waveComposition(wave: number, playerCount: number, rng?: () => number, countMul = 1): WaveGroup[] {
  const n = normaliseWave(wave);
  // `countMul`: the difficulty and the wave ramp (difficulty.ts waveTuning); 1 for callers that only want the table
  const m = playerMultiplier(playerCount);
  const count = (base: number): number => extraCount(scaledCount(base, m), countMul);
  if (isBossWave(n)) return [g('jotunn', 1), ...bossWave(bossTier(n)).escort.map((grp) => g(grp.kind, count(grp.count)))];
  const source = n < FIRST_ENDLESS_WAVE ? scriptedRow(n) : endlessSource(n, rng);
  return scriptedWave(source).groups.map((grp) => g(grp.kind, count(grp.count)));
}

/** The escort a boss calls in when it enrages: its wave's escort again (fresh objects). */
export function bossEscort(wave: number, playerCount: number, countMul = 1): WaveGroup[] {
  const m = playerMultiplier(playerCount);
  return bossWave(Math.max(1, bossTier(wave))).escort.map((grp) => g(grp.kind, extraCount(scaledCount(grp.count, m), countMul)));
}

/** HP multiplier: 1 until the scripted list runs out, then +0.15 a wave (wave 16 is 1.15, as wave 13 was). */
export function waveHpScale(wave: number): number {
  const n = normaliseWave(wave);
  return n < FIRST_ENDLESS_WAVE ? 1 : 1 + ENDLESS_HP_SCALE_PER_WAVE * (n - FIRST_ENDLESS_WAVE + 1);
}

/** The DESIGN 4.4 "Lesson" text for the wave banner; one line for every endless wave. */
export function waveLesson(wave: number): string {
  const n = normaliseWave(wave);
  if (isBossWave(n)) return bossWave(bossTier(n)).lesson;
  return n < FIRST_ENDLESS_WAVE ? scriptedWave(scriptedRow(n)).lesson : ENDLESS_LESSON;
}

/** Total bodies a composition spawns (WaveState.total). */
export function waveTotal(groups: readonly WaveGroup[]): number {
  let total = 0;
  for (const grp of groups) total += grp.count;
  return total;
}
